package com.arena_web.pages

import androidx.compose.runtime.*
import com.arena_web.analytics.AnalyticsTracker
import com.arena_web.data.dto.FeedItemDto
import com.arena_web.data.dto.FeedStoryAuthorGroupDto
import com.arena_web.data.dto.FollowUserDto
import com.arena_web.data.dto.UserCardDto
import com.arena_web.data.dto.UserPostDto
import com.arena_web.data.dto.UserPostImageDto
import com.arena_web.data.dto.UserStoryDto
import com.arena_web.di.LocalAppContainer
import com.arena_web.domain.repositories.FeedRepository
import com.arena_web.ui.components.feed.FeedFollowSidebar
import com.arena_web.ui.components.feed.FeedPostCard
import com.arena_web.ui.components.feed.FeedStoriesRow
import com.arena_web.ui.sections.feed.mobile.FeedContentMobile
import com.arena_web.ui.components.navigation.SidebarTab
import com.arena_web.ui.components.widgets.LoadingSpinner
import com.arena_web.ui.components.overlay.PostCommentsModal
import com.arena_web.ui.components.overlay.StoryViewerModal
import com.arena_web.ui.layouts.AppPageLayout
import com.arena_web.ui.localization.LocalAppStrings
import com.arena_web.ui.theme.AppColors
import com.arena_web.ui.theme.AppTypography
import com.arena_web.ui.utils.*
import com.arena_web.utils.Storage
import com.arena_web.utils.StorageKeys
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.core.Page
import com.varabyte.kobweb.core.rememberPageContext
import com.varabyte.kobweb.silk.components.text.SpanText
import kotlinx.browser.document
import kotlinx.coroutines.async
import kotlinx.coroutines.awaitAll
import kotlinx.coroutines.coroutineScope
import kotlinx.coroutines.launch
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.Div
import org.w3c.dom.events.Event

private const val PAGE_SIZE = 10
private const val STORIES_PAGE_SIZE = 20
private const val FOLLOW_PAGE_SIZE = 20
private const val FEED_SCROLL_ID = "feed-scroll-container"

@Page("/feed")
@Composable
fun FeedPage() {
    val t = LocalAppStrings.current
    val container = LocalAppContainer.current
    val ctx = rememberPageContext()
    val scope = rememberCoroutineScope()
    val mobile = isMobile()

    // Authenticated repos when signed in, public fallbacks for the post feed.
    // Stories + follow sidebar both need auth, so they only render when the
    // authenticated repositories are available.
    val feedRepo: FeedRepository = container.feedRepository ?: container.publicFeedRepository
    val storyRepo = container.userStoryRepository
    val followRepo = container.followRepository
    val currentUserId = Storage.get(StorageKeys.USER_ID)

    var items by remember { mutableStateOf<List<FeedItemDto>>(emptyList()) }
    var isLoading by remember { mutableStateOf(true) }
    // Local refetch indicator for sidebar-selection filter switches. Shown
    // only in the left column (stories + posts), so the sidebar and the page
    // chrome stay interactive while the new filter resolves.
    var isFilterLoading by remember { mutableStateOf(false) }
    var isLoadingMore by remember { mutableStateOf(false) }
    var currentPage by remember { mutableStateOf(1) }
    var hasMore by remember { mutableStateOf(true) }

    // ── Stories state ──────────────────────────────────────────────────────
    var storyGroups by remember { mutableStateOf<List<FeedStoryAuthorGroupDto>>(emptyList()) }
    var storiesPage by remember { mutableStateOf(0) }
    var storiesHasMore by remember { mutableStateOf(true) }
    var isLoadingMoreStories by remember { mutableStateOf(false) }
    var viewerGroupIdx by remember { mutableStateOf<Int?>(null) }
    var viewerInitialStoryIdx by remember { mutableStateOf(0) }
    // "Who saw this story?" sheet (mobile) — the story id whose viewers are
    // shown; non-null = the second sheet is open over the viewer sheet.
    var viewersStoryId by remember { mutableStateOf<String?>(null) }

    // ── Follow sidebar initial state ───────────────────────────────────────
    // Pre-fetched here so the page can gate the screen on ALL initial APIs
    // (posts + stories + following + followers) finishing before content is
    // shown. The sidebar then takes over for load-more on scroll.
    var initialFollowing by remember { mutableStateOf<List<FollowUserDto>>(emptyList()) }
    var initialFollowingTotal by remember { mutableStateOf(0L) }
    var initialFollowingHasMore by remember { mutableStateOf(false) }
    var initialFollowers by remember { mutableStateOf<List<FollowUserDto>>(emptyList()) }
    var initialFollowersTotal by remember { mutableStateOf(0L) }
    var initialFollowersHasMore by remember { mutableStateOf(false) }

    // ── Comments popup state ───────────────────────────────────────────────
    // Tapping a post's chat icon opens the shared PostCommentsModal used by
    // the profile player-hub so the experience matches across screens.
    var commentsTarget by remember { mutableStateOf<FeedItemDto?>(null) }

    // Guest interactions (like/comment/story/author tap) open the
    // AccountRequiredModal popup instead of routing to /login.
    val isLoggedIn = container.feedRepository != null
    var showAuthGate by remember { mutableStateOf(false) }

    // ── Sidebar selection ──────────────────────────────────────────────────
    // When the viewer picks a user from the Following / Followers sidebar we
    // narrow the feed (both stories and posts) down to that user's content
    // via a server-side `authorId` filter. Re-clicking the same user clears
    // the filter and the full feed comes back.
    var selectedUserId by remember { mutableStateOf<String?>(null) }

    fun buildParams(page: Int, authorId: String? = selectedUserId): Map<String, String> {
        val base = mapOf("page" to "$page", "size" to "$PAGE_SIZE")
        return if (authorId.isNullOrBlank()) base else base + ("authorId" to authorId)
    }

    fun storyParams(page: Int, authorId: String? = selectedUserId): Map<String, String> {
        val base = mapOf("page" to "$page", "size" to "$STORIES_PAGE_SIZE")
        return if (authorId.isNullOrBlank()) base else base + ("authorId" to authorId)
    }

    // Initial load: posts + stories + following + followers fire in parallel.
    // Screen waits for ALL of them to settle before flipping `isLoading` off
    // so the UI shows a single loader instead of a half-rendered page.
    LaunchedEffect(feedRepo, storyRepo, followRepo) {
        isLoading = true
        coroutineScope {
            val postsDeferred = async {
                feedRepo.getFeed(buildParams(1, authorId = null)).onSuccess { page ->
                    items = page.items
                    currentPage = 1
                    hasMore = page.items.size >= PAGE_SIZE
                }
            }
            val storiesDeferred = async {
                val repo = storyRepo ?: return@async
                repo.getFeedStoryAuthors(storyParams(1, authorId = null)).onSuccess { page ->
                    storyGroups = page.items
                    storiesPage = 1
                    storiesHasMore = page.items.size >= STORIES_PAGE_SIZE && page.page < page.totalPages
                }
            }
            val followingDeferred = async {
                val repo = followRepo ?: return@async
                val params = mapOf("page" to "1", "size" to "$FOLLOW_PAGE_SIZE")
                repo.getMyFollowing(params).onSuccess { page ->
                    initialFollowing = page.items
                    initialFollowingTotal = page.totalItems
                    initialFollowingHasMore = page.items.size >= FOLLOW_PAGE_SIZE && page.page < page.totalPages
                }
            }
            val followersDeferred = async {
                val repo = followRepo ?: return@async
                val params = mapOf("page" to "1", "size" to "$FOLLOW_PAGE_SIZE")
                repo.getMyFollowers(params).onSuccess { page ->
                    initialFollowers = page.items
                    initialFollowersTotal = page.totalItems
                    initialFollowersHasMore = page.items.size >= FOLLOW_PAGE_SIZE && page.page < page.totalPages
                }
            }
            awaitAll(postsDeferred, storiesDeferred, followingDeferred, followersDeferred)
        }
        isLoading = false
    }

    // Re-fetch posts + stories when the sidebar selection changes. The first
    // composition runs with `selectedUserId == null` and is handled by the
    // initial-load effect above; we gate this one with `skipFirst` so it
    // only fires on subsequent toggles.
    val skipFirstSelection = remember { mutableStateOf(true) }
    LaunchedEffect(selectedUserId) {
        if (skipFirstSelection.value) {
            skipFirstSelection.value = false
            return@LaunchedEffect
        }
        isFilterLoading = true
        // Reset cursors so paging restarts cleanly under the new filter.
        items = emptyList()
        currentPage = 1
        hasMore = true
        storyGroups = emptyList()
        storiesPage = 0
        storiesHasMore = true
        coroutineScope {
            val postsDeferred = async {
                feedRepo.getFeed(buildParams(1)).onSuccess { page ->
                    items = page.items
                    currentPage = 1
                    hasMore = page.items.size >= PAGE_SIZE && page.page < page.totalPages
                }
            }
            val storiesDeferred = async {
                val repo = storyRepo ?: return@async
                repo.getFeedStoryAuthors(storyParams(1)).onSuccess { page ->
                    storyGroups = page.items
                    storiesPage = 1
                    storiesHasMore = page.items.size >= STORIES_PAGE_SIZE && page.page < page.totalPages
                }
            }
            awaitAll(postsDeferred, storiesDeferred)
        }
        isFilterLoading = false
    }

    val loadMoreStories: () -> Unit = {
        val repo = storyRepo
        if (repo != null && storiesHasMore && !isLoadingMoreStories) {
            isLoadingMoreStories = true
            scope.launch {
                val next = storiesPage + 1
                repo.getFeedStoryAuthors(storyParams(next)).onSuccess { page ->
                    storyGroups = storyGroups + page.items
                    storiesPage = next
                    storiesHasMore = page.items.size >= STORIES_PAGE_SIZE && page.page < page.totalPages
                }
                isLoadingMoreStories = false
            }
        }
    }

    val loadMore: () -> Unit = {
        if (!isLoadingMore && hasMore) {
            isLoadingMore = true
            scope.launch {
                val nextPage = currentPage + 1
                feedRepo.getFeed(buildParams(nextPage)).onSuccess { page ->
                    items = items + page.items
                    currentPage = nextPage
                    hasMore = page.items.size >= PAGE_SIZE && page.page < page.totalPages
                }
                isLoadingMore = false
            }
        }
    }

    // Infinite scroll listener on the scroll container
    DisposableEffect(Unit) {
        val handler: (Event) -> Unit = {
            val el = document.getElementById(FEED_SCROLL_ID)
            if (el != null) {
                val scrollTop = el.scrollTop
                val scrollHeight = el.scrollHeight
                val clientHeight = el.clientHeight
                if (scrollHeight - scrollTop - clientHeight < 300) {
                    loadMore()
                }
            }
        }
        val el = document.getElementById(FEED_SCROLL_ID)
        el?.addEventListener("scroll", handler)
        onDispose { el?.removeEventListener("scroll", handler) }
    }

    val onStoryGroupClick: (FeedStoryAuthorGroupDto) -> Unit = lambda@{ group ->
        if (!isLoggedIn) {
            showAuthGate = true
            return@lambda
        }
        // Open the modal at the first unseen story, falling back to the start.
        val firstUnseen = group.stories.indexOfFirst { !it.isSeen }
        viewerInitialStoryIdx = if (firstUnseen >= 0) firstUnseen else 0
        viewerGroupIdx = storyGroups.indexOfFirst { it.authorId == group.authorId }

        // Fire-and-forget the mark-seen calls for every story in the group;
        // the viewer will let the user step through but we can already flip
        // them server-side so the next refresh shows a fully-seen state.
        val repo = storyRepo
        if (repo != null) {
            group.stories.filter { !it.isSeen }.forEach { story ->
                scope.launch { repo.markStorySeen(story.id) }
            }
        }
        // Optimistically mark seen in local state so the tile drops its blur.
        storyGroups = storyGroups.map { g ->
            if (g.authorId == group.authorId) {
                g.copy(stories = g.stories.map { it.copy(isSeen = true) }, allSeen = true)
            } else g
        }
    }

    // ── Post likes ─────────────────────────────────────────────────────────
    // Optimistic like handling shared by the heart icon, the double-tap area
    // and the comments sheet: local state (feed list + comments-sheet mirror)
    // flips immediately. The backend only exposes a toggle endpoint, so
    // requests are serialised per post — parallel toggles from rapid taps used
    // to race each other's responses and make the counter jump.
    val likeSyncInFlight = remember { mutableSetOf<String>() }

    // postId → like state the user last chose, kept until the server confirms
    // (or rejects) it. The reconcile loop compares against this instead of the
    // rendered state, so a concurrent list refetch can't trick it into
    // re-toggling and undoing the user's action.
    val likeDesired = remember { mutableMapOf<String, Boolean>() }

    fun findPost(postId: String): FeedItemDto? =
        items.firstOrNull { it.id == postId } ?: commentsTarget?.takeIf { it.id == postId }

    fun applyPostUpdate(post: FeedItemDto) {
        items = items.map { if (it.id == post.id) post else it }
        commentsTarget = commentsTarget?.let { if (it.id == post.id) post else it }
    }

    // Reconciles the optimistic state with the backend: one request per post at
    // a time; if the user toggled again while a round-trip was in flight, the
    // confirmed `liked` won't match `likeDesired` and one more toggle is sent.
    // On failure the one pending (unconfirmed) toggle is rolled back.
    fun syncLikeWithServer(postId: String, authorType: String) {
        if (!likeSyncInFlight.add(postId)) return
        scope.launch {
            try {
                while (true) {
                    val serverLiked = feedRepo.likePost(postId, authorType)
                        .getOrNull()?.get("liked")
                    if (serverLiked == null) {
                        val desired = likeDesired[postId] ?: return@launch
                        val local = findPost(postId) ?: return@launch
                        if (local.isLikedByMe == desired) {
                            applyPostUpdate(
                                local.copy(
                                    isLikedByMe = !desired,
                                    likesCount = (local.likesCount + if (desired) -1 else 1)
                                        .coerceAtLeast(0),
                                )
                            )
                        }
                        return@launch
                    }
                    if (serverLiked == likeDesired[postId]) return@launch
                }
            } finally {
                likeSyncInFlight.remove(postId)
                likeDesired.remove(postId)
            }
        }
    }

    // Heart-icon tap — optimistic local toggle applied immediately (waiting
    // for the server made the heart feel dead on slow connections).
    val onLikeClickPost: (FeedItemDto) -> Unit = onLike@{ feedItem ->
        if (!isLoggedIn) {
            showAuthGate = true
            return@onLike
        }
        AnalyticsTracker.trackClick("like_post", screen = "feed", params = mapOf("post_id" to feedItem.id))
        val current = findPost(feedItem.id) ?: feedItem
        val nowLiked = !current.isLikedByMe
        likeDesired[current.id] = nowLiked
        applyPostUpdate(
            current.copy(
                isLikedByMe = nowLiked,
                likesCount = (current.likesCount + if (nowLiked) 1 else -1).coerceAtLeast(0),
            )
        )
        syncLikeWithServer(current.id, current.authorType)
    }

    // Instagram-style double-tap like — idempotent (only ever transitions
    // unliked → liked; the backend likePost is a toggle, so we skip it when the
    // post is already liked).
    val onDoubleTapLikePost: (FeedItemDto) -> Unit = onDbl@{ feedItem ->
        if (!isLoggedIn) {
            showAuthGate = true
            return@onDbl
        }
        val current = findPost(feedItem.id) ?: feedItem
        if (current.isLikedByMe) return@onDbl
        AnalyticsTracker.trackClick("like_post", screen = "feed", params = mapOf("post_id" to feedItem.id))
        likeDesired[current.id] = true
        applyPostUpdate(current.copy(isLikedByMe = true, likesCount = current.likesCount + 1))
        syncLikeWithServer(current.id, current.authorType)
    }

    AppPageLayout(
        activeTab = SidebarTab.Events,
        title = t.pageFeedTitle,
        description = t.pageFeedSeoDescription,
        mobileReady = true,
        mobilePageTitle = t.pageFeedTitle,
    ) {
        if (mobile) {
            // Mobile feed — Figma 8278:169490. Stories tiles + post cards in a
            // single scroll container; follow sidebar lives on the dedicated
            // People tab on narrow screens.
            Div(
                attrs = {
                    id(FEED_SCROLL_ID)
                    style {
                        width(100.percent)
                        property("flex-grow", "1")
                        property("min-height", "0")
                        property("overflow-y", "auto")
                        property("overflow-x", "hidden")
                        property("scrollbar-width", "none")
                        property("padding-bottom", "120px")
                    }
                }
            ) {
                if (isLoading || isFilterLoading) {
                    Box(
                        modifier = Modifier.fillMaxWidth().minHeight(240.px),
                        contentAlignment = Alignment.Center
                    ) { LoadingSpinner(size = 80.px) }
                } else {
                    FeedContentMobile(
                        storyGroups = storyGroups,
                        items = items,
                        isLoadingMore = isLoadingMore,
                        onStoryClick = onStoryGroupClick,
                        onAuthorClick = { feedItem ->
                            if (!isLoggedIn) {
                                showAuthGate = true
                            } else if (feedItem.authorType == "USER") {
                                com.arena_web.ui.sections.profile.mobile.openUserProfile(ctx, feedItem.authorId)
                            } else {
                                ctx.router.navigateTo("/clubs/${feedItem.authorId}")
                            }
                        },
                        onLikeClick = onLikeClickPost,
                        onDoubleTapLike = onDoubleTapLikePost,
                        onCommentClick = { feedItem ->
                            if (!isLoggedIn) showAuthGate = true
                            else commentsTarget = feedItem
                        },
                    )
                }
            }
        } else {
            // Desktop: 3-column layout per Figma 8702:137597 — left column
            // holds stories + posts, right column holds the follow sidebar.
            // Both columns scroll independently; only the page itself stays
            // pinned to the viewport.
            Row(
                Modifier.fillMaxWidth().flexGrow(1).styleModifier {
                    property("min-height", "0")
                    property("padding", "${s(20)} ${s(40)} 0 ${s(40)}")
                    property("gap", s(20))
                }
            ) {
                // Left column — stories + posts in a single scroll container.
                Div(
                    attrs = {
                        id(FEED_SCROLL_ID)
                        style {
                            property("flex", "1 1 0")
                            property("min-width", "0")
                            property("height", "100%")
                            property("overflow-y", "auto")
                            property("overflow-x", "hidden")
                            property("scrollbar-width", "thin")
                            property("scrollbar-color", "rgba(255,255,255,0.15) transparent")
                        }
                    }
                ) {
                    // Stories span the full left-column width (left page padding
                    // → Following sidebar gap) per Figma 8702:137551. Posts stay
                    // centered inside via their own inner max-width.
                    Column(
                        Modifier
                            .fillMaxWidth()
                            .fillMaxHeight()
                            .styleModifier {
                                property("gap", s(28))
                                property("padding-bottom", s(40))
                            }
                    ) {
                        if (isLoading || isFilterLoading) {
                            Box(Modifier.fillMaxSize(), contentAlignment = Alignment.Center) {
                                LoadingSpinner(size = 80.px)
                            }
                            return@Column
                        }
                        if (storyGroups.isNotEmpty()) {
                            FeedStoriesRow(
                                groups = storyGroups,
                                onAuthorClick = onStoryGroupClick,
                                onLoadMore = loadMoreStories,
                            )
                        }
                        PostsList(
                            items = items,
                            isLoading = false,
                            isLoadingMore = isLoadingMore,
                            mobile = false,
                            onDoubleTapLike = onDoubleTapLikePost,
                            onLikeClick = onLikeClickPost,
                            onAuthorClick = { feedItem ->
                                if (!isLoggedIn) {
                                    showAuthGate = true
                                } else if (feedItem.authorType == "USER") {
                                    com.arena_web.ui.sections.profile.mobile.openUserProfile(ctx, feedItem.authorId)
                                } else {
                                    ctx.router.navigateTo("/clubs/${feedItem.authorId}")
                                }
                            },
                            onCommentClick = { feedItem ->
                                if (!isLoggedIn) {
                                    showAuthGate = true
                                } else {
                                    commentsTarget = feedItem
                                }
                            },
                        )
                    }
                }

                // Right column — Following / Followers sidebar. Width per
                // Figma 8702:137732 (Profile_Card outer = 593px). The
                // sidebar owns its own scroll. Hidden during the initial
                // page load so the single combined loader covers everything.
                val fr = followRepo
                if (fr != null && !isLoading) {
                    Box(
                        modifier = Modifier
                            .styleModifier {
                                property("flex", "0 0 ${s(593)}")
                                property("height", "100%")
                                property("min-height", "0")
                            }
                    ) {
                        FeedFollowSidebar(
                            followRepository = fr,
                            currentUserId = currentUserId,
                            initialFollowing = initialFollowing,
                            initialFollowingTotal = initialFollowingTotal,
                            initialFollowingHasMore = initialFollowingHasMore,
                            initialFollowers = initialFollowers,
                            initialFollowersTotal = initialFollowersTotal,
                            initialFollowersHasMore = initialFollowersHasMore,
                            selectedUserId = selectedUserId,
                            onUserClick = { user ->
                                // Toggle: clicking the selected user clears the
                                // filter and the full feed comes back.
                                selectedUserId =
                                    if (selectedUserId == user.userId) null else user.userId
                            },
                            onDiscoverPlayersClick = {
                                ctx.router.navigateTo("/people")
                            },
                            modifier = Modifier.fillMaxSize(),
                        )
                    }
                }
            }
        }
    }

    // Story-viewer popup — anchored at page level so its fixed overlay isn't
    // clipped by the scroll container.
    val groupIdx = viewerGroupIdx
    if (groupIdx != null && groupIdx in storyGroups.indices) {
        val group = storyGroups[groupIdx]
        val viewerStories = group.stories.map { s ->
            // Synthesize a UserStoryDto so the existing viewer can consume the
            // enriched feed-story payload without a parallel modal component.
            UserStoryDto(
                id = s.id,
                authorId = group.authorId,
                imageUrl = s.imageUrl,
                createdAt = s.createdAt,
                expiresAt = s.createdAt,
                likesCount = s.likesCount,
                isLikedByMe = s.isLikedByMe,
                viewsCount = s.viewsCount,
            )
        }
        // Synthesize a UserCardDto from the group's player-card fields so the
        // viewer's identity row renders the author's actual card instead of
        // falling back to a plain avatar disc.
        val viewerCard = group.cardImageUrl?.let { frame ->
            UserCardDto(
                name = group.authorName,
                cardImageUrl = frame,
                assetsBasePath = group.cardAssetsBasePath,
                textColor = group.cardTextColor,
                avatarUrl = group.cardAvatarUrl,
            )
        }
        val viewerSportName = Storage.get(StorageKeys.SELECTED_SPORT_NAME)?.lowercase() ?: "all"
        // Optimistic like toggle — flip local state immediately, reconcile with
        // the server, revert on failure. Shared by the sheet and the popup.
        val onStoryLike: ((String) -> Unit)? = storyRepo?.let { repo ->
            { storyId ->
                val before = storyGroups
                storyGroups = storyGroups.map { g ->
                    if (g.authorId != group.authorId) g
                    else g.copy(
                        stories = g.stories.map { st ->
                            if (st.id != storyId) st
                            else st.copy(
                                isLikedByMe = !st.isLikedByMe,
                                likesCount = (st.likesCount + if (st.isLikedByMe) -1 else 1)
                                    .coerceAtLeast(0),
                            )
                        },
                    )
                }
                scope.launch {
                    repo.likeStory(storyId).onFailure { storyGroups = before }
                }
            }
        }
        // Mobile → bottom sheet (with the Seen-by viewers sheet, like the
        // profile); desktop → the centred popup.
        if (mobile) {
            com.arena_web.ui.sections.profile.mobile.MobileStoryViewerSheet(
                stories = viewerStories,
                initialIndex = viewerInitialStoryIdx,
                title = group.authorName,
                sportName = viewerSportName,
                activeCard = viewerCard,
                avatarUrl = group.authorAvatarUrl,
                onDismiss = { viewerGroupIdx = null },
                onLikeClick = onStoryLike,
                onSeenByClick = { id -> viewersStoryId = id },
                externallyPaused = viewersStoryId != null,
            )
        } else {
            StoryViewerModal(
                stories = viewerStories,
                initialIndex = viewerInitialStoryIdx,
                title = group.authorName,
                sportName = viewerSportName,
                activeCard = viewerCard,
                avatarUrl = group.authorAvatarUrl,
                onDismiss = { viewerGroupIdx = null },
                onLikeClick = onStoryLike,
            )
        }
    }

    // "Who saw this story?" — second sheet popped over the mobile viewer on a
    // Seen-by tap. Sibling of the viewer sheet so its fixed scrim isn't trapped.
    viewersStoryId?.let { vsId ->
        com.arena_web.ui.sections.profile.mobile.MobileStoryViewersSheet(
            storyId = vsId,
            storyRepository = storyRepo,
            sportName = Storage.get(StorageKeys.SELECTED_SPORT_NAME)?.lowercase() ?: "all",
            onDismiss = { viewersStoryId = null },
        )
    }

    // Comments surface — the feed payload is richer than UserPostDto, so we
    // project the fields the comment views need through `toUserPostDto()`.
    // Mobile gets the bottom sheet (Figma 8282:171340); desktop keeps the
    // centred modal shared with the profile player-hub.
    val onCommentsChanged: (Int) -> Unit = { newCount ->
        commentsTarget?.let { current ->
            commentsTarget = current.copy(commentsCount = newCount)
            items = items.map { existing ->
                if (existing.id == current.id) existing.copy(commentsCount = newCount)
                else existing
            }
        }
    }
    val postRepo = container.userPostRepository
    if (mobile) {
        val target = commentsTarget
        if (target != null && postRepo != null) {
            com.arena_web.ui.sections.feed.mobile.MobilePostCommentsSheet(
                post = target.toUserPostDto(),
                repository = postRepo,
                onDismiss = { commentsTarget = null },
                onCommentsChanged = onCommentsChanged,
                onLikeClick = { onLikeClickPost(target) },
                // Heart-burst always plays inside the sheet; the like itself is
                // idempotent (shared with the feed list).
                onDoubleTapLike = { onDoubleTapLikePost(target) },
            )
        }
    } else {
        PostCommentsModal(
            isOpen = commentsTarget != null,
            post = commentsTarget?.toUserPostDto(),
            repository = postRepo,
            onDismiss = { commentsTarget = null },
            onCommentsChanged = onCommentsChanged,
        )
    }

    com.arena_web.ui.components.overlay.AccountRequiredModal(
        isOpen = showAuthGate,
        onClose = { showAuthGate = false },
        onSignedIn = {
            showAuthGate = false
            kotlinx.browser.window.location.reload()
        }
    )
}

private fun FeedItemDto.toUserPostDto(): UserPostDto = UserPostDto(
    id = id,
    authorId = authorId,
    content = content,
    likesCount = likesCount,
    isLikedByMe = isLikedByMe,
    commentsCount = commentsCount,
    layout = layout,
    city = city,
    images = images.map { UserPostImageDto(id = it.id, imageUrl = it.imageUrl, sortOrder = it.sortOrder) },
    createdAt = createdAt,
    updatedAt = createdAt,
)

@Composable
private fun PostsList(
    items: List<FeedItemDto>,
    isLoading: Boolean,
    isLoadingMore: Boolean,
    mobile: Boolean,
    onLikeClick: (FeedItemDto) -> Unit,
    onDoubleTapLike: (FeedItemDto) -> Unit,
    onAuthorClick: (FeedItemDto) -> Unit,
    onCommentClick: (FeedItemDto) -> Unit,
) {
    val t = LocalAppStrings.current
    Column(
        Modifier
            .fillMaxWidth()
            .then(
                if (mobile) Modifier.styleModifier {
                    property("gap", "12px")
                } else Modifier.styleModifier {
                    // Width per Figma 8702:137660 (Block_Goals = 741px wide).
                    property("max-width", s(741))
                    property("margin", "0 auto")
                    property("gap", s(20))
                }
            )
    ) {
        when {
            isLoading -> {
                Box(Modifier.fillMaxWidth().minHeight(200.px), contentAlignment = Alignment.Center) {
                    SpanText(
                        t.pageLoading,
                        modifier = Modifier
                            .then(
                                if (mobile) Modifier.fontSize(14.px)
                                else Modifier.sFontSize(16)
                            )
                            .color(AppColors.Text.Subtle)
                    )
                }
            }
            items.isEmpty() -> {
                Box(Modifier.fillMaxWidth().minHeight(200.px), contentAlignment = Alignment.Center) {
                    SpanText(
                        t.pageNoPostsYet,
                        modifier = Modifier
                            .then(
                                if (mobile) Modifier.fontSize(14.px)
                                else Modifier.sFontSize(16)
                            )
                            .color(AppColors.Text.Subtle)
                    )
                }
            }
            else -> {
                items.forEach { feedItem ->
                    FeedPostCard(
                        item = feedItem,
                        onLikeClick = { onLikeClick(feedItem) },
                        onDoubleTapLike = { onDoubleTapLike(feedItem) },
                        onAuthorClick = { onAuthorClick(feedItem) },
                        onCommentClick = { onCommentClick(feedItem) },
                    )
                }

                if (isLoadingMore) {
                    Box(Modifier.fillMaxWidth().padding(top = 16.px), contentAlignment = Alignment.Center) {
                        SpanText(
                            t.pageLoadingMore,
                            modifier = Modifier
                                .then(
                                    if (mobile) Modifier.fontSize(13.px)
                                    else Modifier.sFontSize(14)
                                )
                                .color(AppColors.Text.Subtle)
                        )
                    }
                }
            }
        }
    }
}

